import React, { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { makeStyles, Box } from '@material-ui/core';

import { AuthContext, AuthStatus } from 'src/contexts/authContext';
import { API } from 'aws-amplify';

import { useDispatch } from 'react-redux';
import {
  editUserCompany,
  editUserName,
  editUserEmail,
  editUserGrade
} from 'src/store/modules/client';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { isMobile } from 'react-device-detect';
import { ReactComponent as IAMLogo } from 'src/images/iam_logo.svg';
import { colors } from 'src/constants/theme/colors';
import UiHeader from './UiHeader';
import UiNavLeft from './UiNavLeft';

interface Props {
  header: any;
  onClick: (e: any) => void;
  children?: ReactNode;
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100vw',
    height: '100%',
    minWidth: 1200,
    display: 'flex',
    backgroundColor: colors.background.canvas
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row'
  }
}));

const DashboardLayout: FC<Props> = ({ children }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const authContext = useContext(AuthContext);
  const [expireDate, setExpireDate] = useState('');
  const [exportedCount, setExportedCount] = useState(0);
  ChannelService.loadScript();

  useEffect(() => {
    async function onLoad() {
      try {
        const user = await authContext.getAttributes();

        for (let i = 0; i < user.length; i++) {
          if (user[i].Name === 'custom:Company') {
            dispatch(editUserCompany(user[i].Value));
            break;
          }
        }
        for (let i = 0; i < user.length; i++) {
          if (user[i].Name === 'custom:Name') {
            dispatch(editUserName(user[i].Value));
            break;
          }
        }
        for (let i = 0; i < user.length; i++) {
          if (user[i].Name === 'email') {
            dispatch(editUserEmail(user[i].Value));
            break;
          }
        }
      } catch (err) {}

      try {
        const result = await API.get(
          'iam-editor',
          '/subscription/subscription-info',
          ''
        );

        if (
          result.subscriptionExpireDate &&
          result.subscriptionExpireDate - Date.now() > 0 &&
          !result.subscription
        ) {
          dispatch(editUserGrade('PRO'));
          setExpireDate(
            new Date(result?.subscriptionExpireDate).toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }
            )
          );
        } else if (
          result.subscriptionExpireDate &&
          result.subscriptionExpireDate - Date.now() > 0 &&
          result.subscription
        ) {
          dispatch(editUserGrade('STARTER'));
          setExpireDate(
            new Date(result?.subscriptionExpireDate).toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }
            )
          );
        } else if (
          result.freeTrialExpireDate &&
          result.freeTrialExpireDate - Date.now() > 0
        ) {
          dispatch(editUserGrade('PRO'));
          setExpireDate(
            `${new Date(result?.freeTrialExpireDate).toLocaleDateString(
              'en-US',
              {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }
            )}(free trial)`
          );
        } else {
          dispatch(editUserGrade('FREE'));
          setExpireDate('expired');
          setExportedCount(result.exportCount ?? 0);
        }

        ChannelService.boot({
          pluginKey: process.env.REACT_APP_PLUGIN_KEY,
          memberId: result.userId,
          profile: {
            name: result.companyName,
            email: result.companyEmail
          }
        });
      } catch (err) {}
    }

    if (authContext?.authStatus === AuthStatus.SignedIn) {
      onLoad();
    } else {
      ChannelService.boot({
        pluginKey: process.env.REACT_APP_PLUGIN_KEY
      });
    }

    return () => {
      ChannelService.shutdown();
    };
  }, []);

  const [isContentVisible, setIsContentVisible] = useState(false);

  return !isMobile ? (
    <>
      <Box className={classes.root}>
        <UiHeader
          visible={isContentVisible}
          expireDate={expireDate}
          exportedCount={exportedCount}
        />
        <UiNavLeft
          visible={isContentVisible}
          setVisible={setIsContentVisible}
        />
        <div className={classes.container}>{children}</div>
      </Box>
    </>
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: '100vh'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '50px',

            backgroundColor: colors.background.canvas,
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 100
          }}
        >
          <a
            href="https://www.inappmessage.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IAMLogo style={{ width: '36px' }} />
          </a>
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',

            padding: '25px 10%',
            marginTop: '50px',
            justifyContent: 'space-between',
            gap: '20px'
          }}
        >
          {children}
        </div>
        <div
          style={{
            width: '100%',
            minHeight: '75px',
            backgroundColor: colors.background.canvas,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 0'
          }}
        >
          <p style={{ fontSize: '16px', fontFamily: 'Poppins' }}>
            © 2025 Metric Studio, Inc.
          </p>
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;
